import { useEffect, useRef, useState } from 'preact/hooks';
import { createAtlasChannel, createCoreAtlasChannel, type AtlasChannel } from '../lib/atlasChannel';

/** The ONE atlas-cockpit channel instance for this app session (per tenant).
 * Held in a ref (never recreated across renders) and only ever constructed by
 * whichever component calls this hook first — CockpitFrame, mounted once at
 * the shell level, so the channel it returns (and therefore the column's
 * draft text and conversation) survives every route change underneath it.
 *
 * A1: with a tenant, the channel is the conversation over Core
 * (createCoreAtlasChannel); without one it stays the honest disconnected
 * default. A tenant switch disposes the old channel and starts a new one.
 *
 * Re-renders the caller on any channel mutation (subscribe), since
 * AtlasChannel is a plain mutable object, not Preact state. */
export function useAtlasChannel(tenantId?: string | null): AtlasChannel {
  const ref = useRef<{ tenantId: string | null; channel: AtlasChannel } | null>(null);
  const key = tenantId ?? null;
  if (!ref.current || ref.current.tenantId !== key) {
    ref.current?.channel.dispose?.();
    ref.current = { tenantId: key, channel: key ? createCoreAtlasChannel({ tenantId: key }) : createAtlasChannel() };
  }
  const channel = ref.current.channel;

  const [, bump] = useState(0);
  useEffect(() => channel.subscribe(() => bump((n) => n + 1)), [channel]);
  useEffect(() => () => ref.current?.channel.dispose?.(), []);

  return channel;
}
